<script lang="ts" setup>
import { computed, type ComputedRef } from 'vue';

import { WbIcon } from '@dh-io-owpi/shared/src/components/wb';
import { ResponsiveImg } from '@dh-io-owpi/shared/src/components/ResponsiveImg';
import { type Feature } from '@dh-io-owpi/backend-api/src/data-contracts';
import { mapToPictureProps } from '@/sections/features/utils';

const props = defineProps<Feature & { modelName?: string }>();

defineEmits<{ click: [equipmentId: string] }>();

const hasImage: ComputedRef<boolean> = computed(() => !!props.imageUrl);
const image = computed(() => props.imageUrl && mapToPictureProps(props.imageUrl));
</script>

<template>
  <button
    :class="[
      'wbx-card wbx-card--primary wbx-card--interactive owpi-simple-list-item',
      { 'owpi-simple-list-item--only': !hasImage },
    ]"
    :data-equipmentId="equipmentId"
    @click="$emit('click', equipmentId)"
    @keypress.enter.space="$emit('click', equipmentId)"
  >
    <ResponsiveImg
      v-if="image"
      class="owpi-simple-list-item__media"
      :picture="image"
      :alt="modelName ? `${modelName} ${title}` : title"
      :title
      loading="lazy"
    />

    <section class="owpi-simple-list-item__content">
      <h3 class="wb-text-l-strong">{{ title }}</h3>
      <br />
      <p v-if="description" class="wb-text-l" v-html="description"></p>
    </section>

    <div class="owpi-simple-list-item__btn">
      <div class="wbx-round-button wbx-round-button--elevation-xs wbx-round-button--medium">
        <WbIcon name="bds/chevron-right/24" flipRtl />
      </div>
    </div>
  </button>
</template>

<style lang="scss">
$simpleListSpacing: 8px;
$simpleListTagsMinHeight: 30px;

.owpi-simple-list-item {
  cursor: pointer;

  border: none;

  padding-inline: 0 var(--wb-spacing-xs);
  padding-block: 0;
  text-align: left;
  gap: var(--wb-spacing-xs);
  display: flex;
  align-items: center;

  &--only {
    padding-inline: var(--wb-spacing-xs);
    height: var(--vf-list-item-size);
  }

  &__media {
    object-fit: cover;
    width: var(--vf-list-item-size);
    height: var(--vf-list-item-size);
  }

  &__content {
    --vf-list-item-gap: var(--wb-spacing-3xs);

    flex: 1;
    display: -webkit-box;
    -webkit-line-clamp: var(--vf-list-item-clamp);
    -webkit-box-orient: vertical;
    overflow: hidden;
    word-break: break-word;

    padding-top: var(--vf-list-item-gap);

    h3,
    p {
      display: inline;
    }

    h3 {
      position: relative;
      top: calc(0px - var(--vf-list-item-gap));
    }
    p {
      line-height: 1.5rem;
    }
  }

  @media (pointer: coarse), (max-width: 768px) {
    &__btn {
      display: none;
    }
  }
}
</style>
